import { type LeadStatus } from '@up2stay/up2promo-api-client';
import {
  Button,
  cn,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@up2stay/up2promo-ui';
import {
  Archive,
  ArchiveRestore,
  Calendar,
  CircleDot,
  Download,
  Loader2,
  Tag,
  UserCheck,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  LeadAssigneePicker,
  type LeadAssigneePickerLabels,
} from '@/core/components/leads/lead-assignee-picker';
import { LEAD_STATUS_IDS } from '@/core/components/leads/lead-status-colors';
import {
  leadsBulkToolbarButtonClass,
  leadsBulkToolbarIconButtonClass,
} from '@/core/components/layout/list-toolbar-styles';
import { useIsMobile } from '@/core/hooks/use-mobile';

export type LeadsBulkBarLabels = {
  selected: string;
  selectedOf: string;
  export: string;
  setStatus: string;
  setAssignee: string;
  addTags: string;
  removeTags: string;
  addToEvent: string;
  removeFromEvent: string;
  noEventsToAdd: string;
  noEventsToRemove: string;
  clear: string;
  archive: string;
  /** When set with archiveMode `unarchive`, button uses restore copy/icon. */
  unarchive?: string;
  tagPlaceholder: string;
  applyTag: string;
  existingTags: string;
  noExistingTags: string;
  noTagsToRemove: string;
  updating: string;
  ownershipHint?: string;
  ownershipHintPlural?: string;
};

export type LeadsBulkAssigneeOption = {
  userId: string;
  label: string;
};

export type LeadsBulkEventOption = {
  id: string;
  label: string;
};

type LeadsBulkBarProps = {
  workspaceId: string;
  locale: string;
  selectedCount: number;
  filteredCount: number;
  canUpdate: boolean;
  canExport: boolean;
  /** When false, Event organize actions are hidden (entitlement off). */
  canOrganizeEvents: boolean;
  isUpdating: boolean;
  isExporting: boolean;
  isArchiving?: boolean;
  /** Bulk archive control: archive active leads, or restore when selection is all archived. */
  archiveMode?: 'archive' | 'unarchive';
  statusLabels: Record<string, string>;
  assigneePickerLabels: LeadAssigneePickerLabels;
  /** Counts of selected leads currently assigned per user id. */
  assignmentCountsByUserId: ReadonlyMap<string, number>;
  privateShareCount: number;
  eventOptions: LeadsBulkEventOption[];
  removableEventIds: string[];
  /** Workspace tags available to pick when adding (excludes Event legacy strings). */
  existingTags: string[];
  removableTags: string[];
  labels: LeadsBulkBarLabels;
  /** Count of selected leads owned by someone else (L8 footnote). */
  othersOwnedSelectedCount?: number;
  onClear: () => void;
  onExport: () => void;
  onArchive: () => void;
  onSetStatus: (status: LeadStatus) => void;
  onSetAssignee: (userId: string | null) => void;
  onAddTag: (tag: string) => void;
  onRemoveTag: (tag: string) => void;
  onAddToEvent: (eventId: string) => void;
  onRemoveFromEvent: (eventId: string) => void;
};

export function LeadsBulkBar({
  workspaceId,
  locale,
  selectedCount,
  filteredCount,
  canUpdate,
  canExport,
  canOrganizeEvents,
  isUpdating,
  isExporting,
  isArchiving = false,
  archiveMode = 'archive',
  statusLabels,
  assigneePickerLabels,
  assignmentCountsByUserId,
  privateShareCount,
  eventOptions,
  removableEventIds,
  existingTags,
  removableTags,
  labels,
  othersOwnedSelectedCount = 0,
  onClear,
  onExport,
  onArchive,
  onSetStatus,
  onSetAssignee,
  onAddTag,
  onRemoveTag,
  onAddToEvent,
  onRemoveFromEvent,
}: LeadsBulkBarProps) {
  const isMobile = useIsMobile();
  const [addTagOpen, setAddTagOpen] = useState(false);
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const [tagDraft, setTagDraft] = useState('');

  const selectedLabel = useMemo(
    () => labels.selected.replace('{count}', String(selectedCount)),
    [labels.selected, selectedCount],
  );
  const selectedOfLabel = useMemo(
    () => labels.selectedOf.replace('{total}', String(filteredCount)),
    [labels.selectedOf, filteredCount],
  );

  const removableEvents = useMemo(() => {
    const allow = new Set(removableEventIds);
    return eventOptions.filter((ev) => allow.has(ev.id));
  }, [eventOptions, removableEventIds]);

  const filteredExistingTags = useMemo(() => {
    const q = tagDraft.trim().toLowerCase();
    if (!q) return existingTags;
    return existingTags.filter((tag) => tag.toLowerCase().includes(q));
  }, [existingTags, tagDraft]);

  const applyTag = (tag: string) => {
    const next = tag.trim();
    if (!next) return;
    onAddTag(next);
    setTagDraft('');
    setAddTagOpen(false);
  };

  const busy = isUpdating || isExporting || isArchiving;

  const assigneePanel = workspaceId ? (
    <LeadAssigneePicker
      workspaceId={workspaceId}
      ownerUserId=""
      ownerOptionLabel=""
      assigneeUserId={undefined}
      triggerLabel=""
      placeholder=""
      labels={assigneePickerLabels}
      locale={locale}
      presentation="inline"
      confirmLayout={isMobile ? 'sheet' : 'popover'}
      hideNobody
      selectedCount={selectedCount}
      privateShareCount={privateShareCount}
      assignmentCountsByUserId={assignmentCountsByUserId}
      onAssigneeChange={(userId) => {
        onSetAssignee(userId);
        setAssigneeOpen(false);
      }}
    />
  ) : null;

  if (selectedCount === 0) return null;

  return (
    <div className="flex flex-col gap-0">
      <div className="flex flex-wrap items-center gap-2 bg-[color:var(--surface-2)] px-[14px] py-2.5">
        <span className="text-[13px] font-semibold text-foreground">
          {selectedLabel}
        </span>
        <span className="text-[12px] text-muted-foreground">
          {selectedOfLabel}
        </span>
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2">
          {canUpdate ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={leadsBulkToolbarButtonClass}
                  disabled={busy}
                >
                  {isUpdating ? (
                    <Loader2 className="size-3.5 animate-spin" aria-hidden />
                  ) : (
                    <CircleDot className="size-3.5" aria-hidden />
                  )}
                  {labels.setStatus}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {LEAD_STATUS_IDS.map((status) => (
                  <DropdownMenuItem
                    key={status}
                    onSelect={() => onSetStatus(status)}
                  >
                    {statusLabels[status] ?? status}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}

          {canUpdate ? (
            isMobile ? (
              <>
                <button
                  type="button"
                  className={leadsBulkToolbarButtonClass}
                  disabled={busy || !workspaceId}
                  onClick={() => setAssigneeOpen(true)}
                >
                  <UserCheck className="size-3.5" aria-hidden />
                  {labels.setAssignee}
                </button>
                <Drawer open={assigneeOpen} onOpenChange={setAssigneeOpen}>
                  <DrawerContent fitContent className="rounded-t-3xl">
                    <DrawerHeader className="sr-only">
                      <DrawerTitle>{labels.setAssignee}</DrawerTitle>
                      <DrawerDescription>
                        {labels.setAssignee}
                      </DrawerDescription>
                    </DrawerHeader>
                    {assigneePanel}
                  </DrawerContent>
                </Drawer>
              </>
            ) : (
              <Popover open={assigneeOpen} onOpenChange={setAssigneeOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={leadsBulkToolbarButtonClass}
                    disabled={busy || !workspaceId}
                  >
                    <UserCheck className="size-3.5" aria-hidden />
                    {labels.setAssignee}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-[320px] p-0">
                  {assigneePanel}
                </PopoverContent>
              </Popover>
            )
          ) : null}

          {canUpdate && canOrganizeEvents ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={leadsBulkToolbarButtonClass}
                  disabled={busy}
                >
                  <Calendar className="size-3.5" aria-hidden />
                  {labels.addToEvent}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="max-h-72 w-56 overflow-y-auto"
              >
                <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground">
                  {labels.addToEvent}
                </DropdownMenuLabel>
                {eventOptions.length === 0 ? (
                  <DropdownMenuItem disabled>
                    {labels.noEventsToAdd}
                  </DropdownMenuItem>
                ) : (
                  eventOptions.map((ev) => (
                    <DropdownMenuItem
                      key={`add-${ev.id}`}
                      onSelect={() => onAddToEvent(ev.id)}
                    >
                      {ev.label}
                    </DropdownMenuItem>
                  ))
                )}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground">
                  {labels.removeFromEvent}
                </DropdownMenuLabel>
                {removableEvents.length === 0 ? (
                  <DropdownMenuItem disabled>
                    {labels.noEventsToRemove}
                  </DropdownMenuItem>
                ) : (
                  removableEvents.map((ev) => (
                    <DropdownMenuItem
                      key={`rm-${ev.id}`}
                      onSelect={() => onRemoveFromEvent(ev.id)}
                    >
                      {ev.label}
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}

          {canUpdate ? (
            <Popover open={addTagOpen} onOpenChange={setAddTagOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={leadsBulkToolbarButtonClass}
                  disabled={busy}
                >
                  <Tag className="size-3.5" aria-hidden />
                  {labels.addTags}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 space-y-2 p-3">
                <Input
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  placeholder={labels.tagPlaceholder}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyTag(tagDraft);
                    }
                  }}
                />
                <div className="space-y-1">
                  <p className="px-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {labels.existingTags}
                  </p>
                  <div className="max-h-40 space-y-0.5 overflow-y-auto">
                    {filteredExistingTags.length === 0 ? (
                      <p className="px-1 py-1.5 text-sm text-muted-foreground">
                        {labels.noExistingTags}
                      </p>
                    ) : (
                      filteredExistingTags.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          className="flex w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
                          onClick={() => applyTag(tag)}
                        >
                          {tag}
                        </button>
                      ))
                    )}
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  className="w-full"
                  disabled={!tagDraft.trim()}
                  onClick={() => applyTag(tagDraft)}
                >
                  {labels.applyTag}
                </Button>
                {removableTags.length > 0 ? (
                  <>
                    <div
                      className="h-px bg-border"
                      role="separator"
                      aria-hidden
                    />
                    <p className="px-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {labels.removeTags}
                    </p>
                    <div className="max-h-32 space-y-0.5 overflow-y-auto">
                      {removableTags.map((tag) => (
                        <button
                          key={`rm-${tag}`}
                          type="button"
                          className="flex w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
                          onClick={() => {
                            onRemoveTag(tag);
                            setAddTagOpen(false);
                          }}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </>
                ) : null}
              </PopoverContent>
            </Popover>
          ) : null}

          {canExport ? (
            <button
              type="button"
              className={leadsBulkToolbarIconButtonClass}
              disabled={busy}
              aria-label={labels.export}
              title={labels.export}
              onClick={onExport}
            >
              {isExporting ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
              ) : (
                <Download className="size-3.5" aria-hidden />
              )}
            </button>
          ) : null}

          {canUpdate ? (
            <button
              type="button"
              className={leadsBulkToolbarIconButtonClass}
              disabled={busy}
              aria-label={
                archiveMode === 'unarchive'
                  ? (labels.unarchive ?? labels.archive)
                  : labels.archive
              }
              title={
                archiveMode === 'unarchive'
                  ? (labels.unarchive ?? labels.archive)
                  : labels.archive
              }
              onClick={onArchive}
            >
              {isArchiving ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
              ) : archiveMode === 'unarchive' ? (
                <ArchiveRestore className="size-3.5" aria-hidden />
              ) : (
                <Archive className="size-3.5" aria-hidden />
              )}
            </button>
          ) : null}

          <button
            type="button"
            className={cn(leadsBulkToolbarIconButtonClass)}
            disabled={busy}
            aria-label={labels.clear}
            title={labels.clear}
            onClick={onClear}
          >
            <X className="size-3.5" aria-hidden />
          </button>
        </div>
      </div>
      {othersOwnedSelectedCount > 0 && labels.ownershipHint ? (
        <p className="border-b border-[color:var(--surface-border)] bg-[color:var(--surface-2)] px-[14px] pb-2.5 text-[12px] text-muted-foreground">
          {(othersOwnedSelectedCount === 1
            ? labels.ownershipHint
            : (labels.ownershipHintPlural ?? labels.ownershipHint)
          ).replace('{count}', String(othersOwnedSelectedCount))}
        </p>
      ) : null}
    </div>
  );
}
